/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ChannelMessage } from '../models/channel-message';
import { ChannelMessagePartial } from '../models/channel-message-partial';
import { ChannelMessageWithRelations } from '../models/channel-message-with-relations';
import { Count } from '../models/count';
import { NewChannelMessage } from '../models/new-channel-message';

@Injectable({
  providedIn: 'root',
})
export class ChannelMessageControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation channelMessageControllerCreateAll
   */
  static readonly ChannelMessageControllerCreateAllPath = '/channel-messages/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll$Response(params?: {
    body?: Array<NewChannelMessage>
  }): Observable<StrictHttpResponse<Array<ChannelMessage>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelMessage>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll(params?: {
    body?: Array<NewChannelMessage>
  }): Observable<Array<ChannelMessage>> {

    return this.createAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelMessage>>) => r.body as Array<ChannelMessage>)
    );
  }

  /**
   * Path part for operation channelMessageControllerCount
   */
  static readonly ChannelMessageControllerCountPath = '/channel-messages/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<Count> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation channelMessageControllerFindById
   */
  static readonly ChannelMessageControllerFindByIdPath = '/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ChannelMessageWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelMessageWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ChannelMessageWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelMessageWithRelations>) => r.body as ChannelMessageWithRelations)
    );
  }

  /**
   * Path part for operation channelMessageControllerReplaceById
   */
  static readonly ChannelMessageControllerReplaceByIdPath = '/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById$Response(params: {
    id: string;
    body?: ChannelMessage
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById(params: {
    id: string;
    body?: ChannelMessage
  }): Observable<void> {

    return this.replaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerDeleteById
   */
  static readonly ChannelMessageControllerDeleteByIdPath = '/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerUpdateById
   */
  static readonly ChannelMessageControllerUpdateByIdPath = '/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ChannelMessagePartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ChannelMessagePartial
  }): Observable<void> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerFind
   */
  static readonly ChannelMessageControllerFindPath = '/channel-messages';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ChannelMessageWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelMessageWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<ChannelMessageWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelMessageWithRelations>>) => r.body as Array<ChannelMessageWithRelations>)
    );
  }

  /**
   * Path part for operation channelMessageControllerCreate
   */
  static readonly ChannelMessageControllerCreatePath = '/channel-messages';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewChannelMessage
  }): Observable<StrictHttpResponse<ChannelMessage>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelMessage>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewChannelMessage
  }): Observable<ChannelMessage> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelMessage>) => r.body as ChannelMessage)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyCreateAll
   */
  static readonly ChannelMessageControllerProxyCreateAllPath = '/proxy/channel-messages/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll$Response(params?: {
    body?: Array<NewChannelMessage>
  }): Observable<StrictHttpResponse<Array<ChannelMessage>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelMessage>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll(params?: {
    body?: Array<NewChannelMessage>
  }): Observable<Array<ChannelMessage>> {

    return this.proxyCreateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelMessage>>) => r.body as Array<ChannelMessage>)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyCount
   */
  static readonly ChannelMessageControllerProxyCountPath = '/proxy/channel-messages/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount(params?: {
    where?: any;
  }): Observable<Count> {

    return this.proxyCount$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyFindById
   */
  static readonly ChannelMessageControllerProxyFindByIdPath = '/proxy/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ChannelMessageWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelMessageWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById(params: {
    id: string;
    filter?: any;
  }): Observable<ChannelMessageWithRelations> {

    return this.proxyFindById$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelMessageWithRelations>) => r.body as ChannelMessageWithRelations)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyReplaceById
   */
  static readonly ChannelMessageControllerProxyReplaceByIdPath = '/proxy/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyReplaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById$Response(params: {
    id: string;
    body?: ChannelMessage
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyReplaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById(params: {
    id: string;
    body?: ChannelMessage
  }): Observable<void> {

    return this.proxyReplaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyDeleteById
   */
  static readonly ChannelMessageControllerProxyDeleteByIdPath = '/proxy/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.proxyDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyUpdateById
   */
  static readonly ChannelMessageControllerProxyUpdateByIdPath = '/proxy/channel-messages/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById$Response(params: {
    id: string;
    body?: ChannelMessagePartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById(params: {
    id: string;
    body?: ChannelMessagePartial
  }): Observable<void> {

    return this.proxyUpdateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyFind
   */
  static readonly ChannelMessageControllerProxyFindPath = '/proxy/channel-messages';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFind()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ChannelMessageWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ChannelMessageWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFind$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind(params?: {
    filter?: any;
  }): Observable<Array<ChannelMessageWithRelations>> {

    return this.proxyFind$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ChannelMessageWithRelations>>) => r.body as Array<ChannelMessageWithRelations>)
    );
  }

  /**
   * Path part for operation channelMessageControllerProxyCreate
   */
  static readonly ChannelMessageControllerProxyCreatePath = '/proxy/channel-messages';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate$Response(params?: {
    body?: NewChannelMessage
  }): Observable<StrictHttpResponse<ChannelMessage>> {

    const rb = new RequestBuilder(this.rootUrl, ChannelMessageControllerService.ChannelMessageControllerProxyCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ChannelMessage>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate(params?: {
    body?: NewChannelMessage
  }): Observable<ChannelMessage> {

    return this.proxyCreate$Response(params).pipe(
      map((r: StrictHttpResponse<ChannelMessage>) => r.body as ChannelMessage)
    );
  }

}
